import React, { useState } from 'react';
import Button from 'common/button/button';
import { IGetPaystubPayInfoRes } from '~/types/paystub';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

import { ReactComponent as PaystubDeleteIcon } from 'assets/icons/paystub-delete.svg';
interface IPaystubPaymentCustomTableProps {
  pay2_flag: boolean;
  payment: IGetPaystubPayInfoRes['payment'];
  setPayment: (payment: IGetPaystubPayInfoRes['payment']) => void;
}

// 지급항목 추가 모달 컴포넌트
interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (itemName: string) => boolean;
}

const AddItemModal = ({ isOpen, onClose, onAdd }: AddItemModalProps) => {
  const [itemName, setItemName] = useState('');

  if (!isOpen) return null;

  const handleAdd = () => {
    if (itemName) {
      const success = onAdd(itemName);
      if (success) {
        setItemName('');
        onClose();
      }
    }
  };

  const handleClose = () => {
    setItemName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative z-10 flex w-[330px] flex-col gap-5 rounded-xl bg-white px-10 py-5">
        <p className="text-center text-base font-bold leading-[22px] text-[#333]">지급항목 추가</p>
        <div className="flex items-center justify-center">
          <input
            type="text"
            value={itemName}
            onChange={(e) => {
              if (e.target.value.length <= 7) {
                setItemName(e.target.value);
              }
            }}
            placeholder="지급항목을 입력하세요"
            maxLength={7}
            className="w-[250px] rounded border border-[#e1e1e1] bg-white px-3.5 py-2 text-sm font-medium leading-5 text-[#505054] outline-none placeholder:text-[#999]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
        </div>
        <div className="flex w-full gap-3">
          <button
            onClick={handleClose}
            className="flex h-[34px] flex-1 items-center justify-center gap-0.5 rounded-md bg-[#eff0f0] p-2">
            <p className="text-center text-xs font-bold leading-[18px] text-[#505054]">닫기</p>
          </button>
          <button
            onClick={handleAdd}
            className="flex h-[34px] flex-1 items-center justify-center gap-0.5 rounded-md bg-[#075453] p-2">
            <p className="text-center text-xs font-medium leading-[18px] text-white">추가</p>
          </button>
        </div>
      </div>
    </div>
  );
};

const PaystubPaymentCustomTable = ({ pay2_flag, payment, setPayment }: IPaystubPaymentCustomTableProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setModalState } = useAlertConfirmStore();

  const handleNumberInput = (value: string, callback?: (num: number) => void) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    callback?.(Number(numericValue) || 0);
  };

  const handleAddItem = (itemName: string): boolean => {
    // 중복 체크
    const isDuplicate = payment.payment_items.some((item) => item.name === itemName);

    if (isDuplicate) {
      setModalState({
        isModalShow: true,
        title: '중복된 항목',
        desc: '이미 존재하는 항목명입니다.',
        closeBtnText: '확인',
      });
      return false;
    }

    // 새 항목 추가 로직
    const newItem = {
      name: itemName,
      value: 0,
      type: 'TAXABLE' as const,
    };
    const updatedItems = [...payment.payment_items, newItem];
    setPayment({ ...payment, payment_items: updatedItems });
    return true;
  };

  const handleDeleteItem = (index: number) => {
    setModalState({
      isModalShow: true,
      title: '지급항목 삭제',
      desc: '삭제된 지급항목은 되돌릴 수 없어요',
      closeBtnText: '취소',
      okBtnText: '삭제',
      okCallback: () => {
        const updatedItems = payment.payment_items.filter((_, i) => i !== index);
        setPayment({ ...payment, payment_items: updatedItems });
      },
    });
  };

  const handleItemValueChange = (index: number, value: number) => {
    const updatedItems = [...payment.payment_items];
    updatedItems[index] = { ...updatedItems[index], value };
    setPayment({ ...payment, payment_items: updatedItems });
  };

  return (
    <>
      <div className="mt-4 flex flex-col overflow-hidden rounded-lg border border-[#e1e1e1]">
        {/* Header */}
        <div className="flex w-full items-center border-b border-[#e1e1e1] bg-[#f2f2f2]">
          <div className="flex h-12 w-[60px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#505054]">구분</p>
          </div>
          <div className="flex h-12 w-[140px] items-center justify-center border-r border-[#e1e1e1]">
            <p className="text-sm font-bold text-[#505054]">항목</p>
          </div>
          <div className="flex h-12 flex-1 items-center justify-center">
            <p className="text-sm font-bold text-[#505054]">입력</p>
          </div>
        </div>

        {/* Body */}
        <div className="flex w-full bg-white">
          {/* Left Category */}
          <div className="flex w-[60px] items-center justify-center rounded-bl-lg border-r border-[#e1e1e1] px-2 py-4">
            <p className="text-center text-sm font-bold leading-5 text-[#505054]">
              지급
              <br />
              항목
              <br />
              추가
            </p>
          </div>

          {/* Items Column */}
          <div className="flex flex-col">
            {payment.payment_items.map((item, index) => (
              <div
                key={index}
                className="flex h-[45px] w-[140px] items-center justify-center border-b border-r border-[#e1e1e1] px-2 py-4">
                <p className="text-sm font-medium text-[#505054]">{item.name}</p>
              </div>
            ))}
            {/* Empty space for + 추가하기 button */}
            <div className="h-[45px] w-[140px]" />
          </div>

          {/* Input Column */}
          <div className="flex flex-1 flex-col">
            {payment.payment_items.map((item, index) => (
              <div key={index} className="flex h-[45px] w-full items-center border-b border-[#e1e1e1] px-2 py-4">
                <div className="flex h-[30px] flex-1 items-center justify-end gap-2 rounded border border-[#e1e1e1] bg-white px-2 py-2.5">
                  <input
                    type="text"
                    value={item.value || 0}
                    onChange={(e) => handleNumberInput(e.target.value, (value) => handleItemValueChange(index, value))}
                    className="w-full bg-transparent text-right text-xs leading-[18px] text-[#505054] outline-none placeholder:text-[#b8b8b8]"
                    placeholder="0"
                  />
                </div>
                {/* Delete button for TAXABLE items only */}
                {item.type === 'TAXABLE' && (
                  <button
                    onClick={() => handleDeleteItem(index)}
                    className="ml-2 flex h-6 w-6 items-center justify-center">
                    <PaystubDeleteIcon />
                  </button>
                )}
              </div>
            ))}
            {/* Add button row */}
            <div className="flex h-[45px] w-full items-center justify-end rounded-br-lg px-2.5 py-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex h-[35px] items-center justify-center gap-0.5 rounded-lg px-0 py-2">
                <p className="text-center text-base font-medium leading-[22px] text-[#075453]">+ 추가하기</p>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      <AddItemModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={handleAddItem} />
    </>
  );
};

export default PaystubPaymentCustomTable;
